import React, { useMemo } from 'react';
import { Persona, PersonaMetrics } from '../../types/persona';
import { PersonaAvatar } from './PersonaAvatar';
import { PersonaCapabilityMeter } from './PersonaCapabilityMeter';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { MigrateToForgeButton } from './MigrateToForgeButton';
import { migrateCapabilities, getCapabilityScore } from '../../utils/persona_engine_utils';
import { CapabilityEntry } from '../../types/persona_engine';
import UserBadge from '../common/UserBadge';
import { FiTrash2 } from 'react-icons/fi';

// ── Tag → accent color map ────────────────────────────────────────────────────
const TAG_ACCENT_MAP: Array<{ keywords: string[]; color: string }> = [
  { keywords: ['tech', 'code', 'engineer', 'dev', 'software'], color: '#3b82f6' },   // blue
  { keywords: ['creative', 'write', 'story', 'content', 'author'], color: '#a855f7' }, // purple
  { keywords: ['data', 'analytics', 'science', 'ml', 'ai'], color: '#6366f1' },       // indigo
  { keywords: ['design', 'ux', 'visual', 'ui', 'art'], color: '#ec4899' },            // pink
  { keywords: ['finance', 'legal', 'compliance', 'law', 'audit'], color: '#f59e0b' }, // amber
  { keywords: ['health', 'medical', 'clinical', 'bio'], color: '#10b981' },           // green
];
const DEFAULT_ACCENT = '#6b7280'; // gray

function getAccentColor(tags: string[]): string {
  const lower = tags.map(t => t.toLowerCase());
  for (const { keywords, color } of TAG_ACCENT_MAP) {
    if (lower.some(t => keywords.some(k => t.includes(k)))) return color;
  }
  return DEFAULT_ACCENT;
}

// ── Relative time helper ──────────────────────────────────────────────────────
function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.floor(diff / 3_600_000);
  if (h < 1) return '<1h ago';
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return `${Math.floor(d / 30)}mo ago`;
}

// ── Props ─────────────────────────────────────────────────────────────────────
interface PersonaCardProps {
  persona: Persona;
  metrics?: PersonaMetrics;
  isSelected?: boolean;
  onSelect?: (personaId: string) => void;
  onViewDetails?: (personaId: string) => void;
  onNavigateToDetails?: (personaId: string) => void;
  onDelete?: (persona: Persona) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────
export const PersonaCard: React.FC<PersonaCardProps> = ({
  persona,
  metrics,
  isSelected = false,
  onSelect,
  onViewDetails,
  onNavigateToDetails,
  onDelete,
}) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();

  // ── Forge data ──────────────────────────────────────────────────────────────
  const forge = useMemo(() => {
    const p = persona as any;

    console.group(`[PersonaCard] forge resolution — ${persona.name}`);
    console.log('persona.data:', p?.data);
    console.log('path A (flat)  data.engineering_profile.current_state:',
      p?.data?.engineering_profile?.current_state ?? 'MISS');
    console.log('path B (nested) data.data.engineering_profile.current_state:',
      p?.data?.data?.engineering_profile?.current_state ?? 'MISS');
    console.groupEnd();

    return (
      p?.data?.engineering_profile?.current_state
      ?? p?.data?.data?.engineering_profile?.current_state
      ?? null
    );
  }, [persona]);

  // ── Derived display values ──────────────────────────────────────────────────
  const displayName = forge?.display_name || persona.name;
  const primarySpecialization =
    persona.primarySpecialization ||
    (persona.tags?.length ? persona.tags[0] : 'general');
  const description = (() => {
    // For forge personas, prefer character_bible or system_prompt for the actual
    // description — forge.description on remixes is just "Remixed from X" boilerplate.
    const isRemixBoilerplate = forge?.description &&
      /^remix(ed)?\s+(of|from)\s+/i.test(forge.description);

    if (forge?.description && !isRemixBoilerplate) return forge.description;

    if (forge?.character_bible) {
      const first = forge.character_bible.split('\n\n')[0].trim();
      return first.length > 300 ? first.substring(0, 300) + '…' : first;
    }
    if (forge?.system_prompt) {
      const first = forge.system_prompt.split('\n\n')[0].trim();
      return first.length > 300 ? first.substring(0, 300) + '…' : first;
    }
    if (persona.shortDescription) return persona.shortDescription;
    if (persona.fullDescription) return persona.fullDescription.substring(0, 300);
    return 'No description available.';
  })();

  const accentColor = getAccentColor(persona.tags || []);

  // ── Forge health signals ────────────────────────────────────────────────────
  const hasForge = !!forge;
  const hasEngineeringProfile = !!forge;

  const tokenEstimate = forge?.system_prompt
    ? Math.round(forge.system_prompt.length / 4)
    : null;

  const isStale =
    forge?.last_compiled_source_hash &&
    forge?.system_prompt_hash &&
    forge.last_compiled_source_hash !== forge.system_prompt_hash;

  const isCompiled = !!forge?.system_prompt;

  const controlCount = forge?.ui_schema?.length ?? 0;
  const nonDefaultControls = forge?.ui_schema
    ? forge.ui_schema.filter((c: any) => c.current_value !== c.default_value).length
    : 0;

  const probeCount = forge?.probes?.length ?? 0;
  const probesPassed = forge?.probes
    ? forge.probes.filter((p: any) => p.last_passed === true).length
    : 0;
  const probesFailed = forge?.probes
    ? forge.probes.filter((p: any) => p.last_passed === false).length
    : 0;
  const probesUntested = probeCount - probesPassed - probesFailed;

  // Glow ring: >80% probes passing (and at least 1 probe run)
  const showGlowRing =
    probeCount > 0 &&
    probesUntested === 0 &&
    probesFailed === 0 &&
    probesPassed / probeCount > 0.8;

  const revision = forge?.revision ?? null;
  const isRemix = !!forge?.origin_id;
  const lastSaved = forge?.last_saved ? relativeTime(forge.last_saved) : null;

  // ── Resolved fields — forge takes priority over flat persona fields ─────────
  const resolvedPatterns    = forge?.patterns     ?? persona.patterns     ?? [];
  const resolvedAnti        = forge?.antipatterns ?? persona.antipatterns ?? [];
  const resolvedTethics     = forge?.tethics      ?? persona.tethics      ?? [];
  const resolvedCapabilities: Record<string, CapabilityEntry> = useMemo(() => {
    const src = forge?.capabilities ?? persona.capabilities ?? {};
    return migrateCapabilities(src);
  }, [forge?.capabilities, persona.capabilities]);

  // ── Top capabilities — show more when no forge strip to fill space ──────────
  const capLimit = hasForge ? 3 : 5;
  const topCapabilities = useMemo(() => {
    return Object.entries(resolvedCapabilities)
      .filter(([, entry]) => entry.score >= 1)
      .sort(([, a], [, b]) => b.score - a.score)
      .slice(0, capLimit) as [string, CapabilityEntry][];
  }, [resolvedCapabilities, capLimit]);

  const totalCapCount = Object.values(resolvedCapabilities)
    .filter(entry => entry.score >= 1).length;
  const extraCapCount = Math.max(0, totalCapCount - topCapabilities.length);

  // ── Metadata chip counts ────────────────────────────────────────────────────
  const patternCount = resolvedPatterns.length;
  const antiCount    = resolvedAnti.length;
  const tethicsCount = resolvedTethics.length;
  const hasMetaChips = patternCount + antiCount + tethicsCount > 0;

  // ── Tool counts ─────────────────────────────────────────────────────────────
  const allowCount = (persona as any).allow?.length ?? 0;
  const denyCount = (persona as any).deny?.length ?? 0;
  const hasTools = allowCount + denyCount > 0;

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleCardClick = () => onSelect?.(persona.id);

  const stop =
    (fn: () => void) =>
    (e: React.MouseEvent) => {
      e.stopPropagation();
      fn();
    };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div
      onClick={handleCardClick}
      className={[
        'group',
        'w-full min-h-[420px]',
        'flex flex-col',
        'rounded-xl shadow-lg border overflow-hidden',
        'transition-all duration-200',
        'hover:-translate-y-0.5 hover:shadow-xl',
        onSelect ? 'cursor-pointer' : '',
        isSelected ? 'ring-2 ring-blue-500 ring-offset-2' : '',
        darkMode
          ? 'bg-gray-800 border-gray-700 text-white'
          : 'bg-white border-gray-200 text-gray-900',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* ── Left accent strip ─────────────────────────────────────────────── */}
      <div className="flex flex-1 min-h-0">
        <div
          className="w-1 flex-shrink-0"
          style={{ backgroundColor: accentColor }}
        />

        {/* ── Card body ───────────────────────────────────────────────────── */}
        <div className="flex flex-col flex-1 min-w-0 px-4 pt-4 pb-3 gap-0">

          {/* ── 1. Header band (~100px) ──────────────────────────────────── */}
          <div className="flex-shrink-0 flex items-start gap-3 mb-2" style={{ minHeight: 72 }}>
            {/* Avatar + glow ring */}
            <div
              className={[
                'flex-shrink-0 rounded-full',
                showGlowRing ? 'ring-2 ring-green-400 ring-offset-1' : '',
              ].join(' ')}
            >
              <PersonaAvatar
                name={displayName}
                specialization={primarySpecialization}
                avatarUrl={persona.avatarUrl}
                size="medium"
              />
            </div>

            {/* Name + badges + tags */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <h3
                  className="font-bold text-base truncate bg-clip-text text-transparent"
                  style={{
                    backgroundImage: `linear-gradient(to right, ${accentColor}, #a855f7)`,
                  }}
                >
                  {displayName}
                </h3>
                {revision !== null && revision > 1 && (
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                      darkMode
                        ? 'bg-gray-700 text-gray-400'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    rev.{revision}
                  </span>
                )}
                {isRemix && (
                  <span title="Remixed persona" className="text-xs flex-shrink-0">
                    🔁
                  </span>
                )}
              </div>

              {/* Owner badge */}
              {persona.owner !== undefined && (
                <div className="mt-0.5">
                  <UserBadge owner={persona.owner} size="sm" />
                </div>
              )}

              {/* Tags — max 3, overflow pill */}
              <div className="flex items-center gap-1 mt-1 overflow-hidden" style={{ height: 22 }}>
                {(persona.tags || []).slice(0, 3).map((tag, i) => (
                  <span
                    key={i}
                    className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                      darkMode
                        ? 'bg-gray-700 text-gray-300'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {tag}
                  </span>
                ))}
                {(persona.tags || []).length > 3 && (
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                      darkMode
                        ? 'bg-gray-700 text-gray-400'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    +{persona.tags!.length - 3}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* ── 2. Description strip ─────────────────────────────────────── */}
          <div
            className={`flex-shrink-0 border-t pt-2 mb-2 overflow-hidden ${
              darkMode ? 'border-gray-700' : 'border-gray-100'
            }`}
          >
            <p
              className={`text-xs leading-relaxed overflow-hidden ${
                hasForge ? 'line-clamp-3' : 'line-clamp-4'
              } ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}
            >
              {description}
            </p>
            {/* Compiled / stale badge */}
            <div className="mt-1 flex items-center gap-2">
              {isStale ? (
                <span
                  className={`text-xs px-1.5 py-0.5 rounded ${
                    darkMode
                      ? 'bg-amber-900/40 text-amber-400'
                      : 'bg-amber-50 text-amber-600 border border-amber-200'
                  }`}
                >
                  ⚠ Stale
                </span>
              ) : isCompiled && tokenEstimate !== null ? (
                <span
                  className={`text-xs px-1.5 py-0.5 rounded ${
                    darkMode
                      ? 'bg-indigo-900/40 text-indigo-300'
                      : 'bg-indigo-50 text-indigo-600 border border-indigo-200'
                  }`}
                >
                  ⚡ ~{tokenEstimate.toLocaleString()} tokens
                </span>
              ) : null}
            </div>
          </div>

          {/* ── 3. Forge health strip (~36px) ────────────────────────────── */}
          {hasForge && (
            <div
              className={`flex-shrink-0 border-t pt-2 mb-2 ${
                darkMode ? 'border-gray-700' : 'border-gray-100'
              }`}
              style={{ height: 36 }}
            >
              <div className="flex items-center gap-2 overflow-x-auto scrollbar-none text-xs">
                {controlCount > 0 && (
                  <span
                    className={`flex-shrink-0 px-1.5 py-0.5 rounded-full ${
                      darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                    }`}
                    title={`${controlCount} controls, ${nonDefaultControls} non-default active`}
                  >
                    🎛 {controlCount}
                    {nonDefaultControls > 0 && (
                      <span className="ml-0.5 text-amber-400">●</span>
                    )}
                  </span>
                )}
                {probeCount > 0 && (
                  <span
                    className={`flex-shrink-0 px-1.5 py-0.5 rounded-full ${
                      probesUntested === probeCount
                        ? darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
                        : probesFailed > 0
                          ? darkMode ? 'bg-red-900/40 text-red-400' : 'bg-red-50 text-red-600'
                          : darkMode ? 'bg-green-900/40 text-green-400' : 'bg-green-50 text-green-600'
                    }`}
                    title={`${probesPassed} passed · ${probesFailed} failed · ${probesUntested} untested`}
                  >
                    🔬 {probeCount}
                  </span>
                )}
                {lastSaved && (
                  <span
                    className={`flex-shrink-0 px-1.5 py-0.5 rounded-full ${
                      darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    🕐 {lastSaved}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* ── 4. Capability bars ───────────────────────────────────────── */}
          <div
            className={`flex-shrink-0 border-t pt-2 mb-2 ${
              darkMode ? 'border-gray-700' : 'border-gray-100'
            }`}
          >
            {topCapabilities.length > 0 ? (
              <div className="space-y-1.5">
                {topCapabilities.map(([key, entry]) => (
                  <PersonaCapabilityMeter
                    key={key}
                    label={entry.label || key.replace(/_/g, ' ')}
                    value={entry.score}
                    max={10}
                  />
                ))}
                {extraCapCount > 0 && (
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    +{extraCapCount} more
                  </p>
                )}
              </div>
            ) : (
              <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                No capability data
              </p>
            )}
          </div>

          {/* ── 5. Metadata chips (~36px) ────────────────────────────────── */}
          {hasMetaChips && (
            <div
              className={`flex-shrink-0 border-t pt-2 mb-2 overflow-hidden ${
                darkMode ? 'border-gray-700' : 'border-gray-100'
              }`}
              style={{ height: 36 }}
            >
              <div className="flex items-center gap-2 text-xs overflow-hidden">
                {patternCount > 0 && (
                  <span
                    title={`${patternCount} patterns`}
                    className={`flex-shrink-0 px-1.5 py-0.5 rounded-full ${
                      darkMode
                        ? 'bg-blue-900/40 text-blue-300'
                        : 'bg-blue-50 text-blue-600 border border-blue-200'
                    }`}
                  >
                    ✦ {patternCount} patterns
                  </span>
                )}
                {antiCount > 0 && (
                  <span
                    title={`${antiCount} antipatterns`}
                    className={`flex-shrink-0 px-1.5 py-0.5 rounded-full ${
                      darkMode
                        ? 'bg-red-900/40 text-red-300'
                        : 'bg-red-50 text-red-600 border border-red-200'
                    }`}
                  >
                    ⊘ {antiCount} anti
                  </span>
                )}
                {tethicsCount > 0 && (
                  <span
                    title={`${tethicsCount} technical ethics`}
                    className={`flex-shrink-0 px-1.5 py-0.5 rounded-full ${
                      darkMode
                        ? 'bg-green-900/40 text-green-300'
                        : 'bg-green-50 text-green-600 border border-green-200'
                    }`}
                  >
                    ⚖ {tethicsCount} tethics
                  </span>
                )}
              </div>
            </div>
          )}

          {/* ── 6. Tool strip (~28px) ────────────────────────────────────── */}
          {hasTools && (
            <div
              className={`flex-shrink-0 border-t pt-2 mb-2 ${
                darkMode ? 'border-gray-700' : 'border-gray-100'
              }`}
              style={{ height: 28 }}
            >
              <div className="flex items-center gap-3 text-xs">
                {allowCount > 0 && (
                  <span
                    className={darkMode ? 'text-gray-400' : 'text-gray-500'}
                    title={`${allowCount} allowed tools`}
                  >
                    🔧 {allowCount} allowed
                  </span>
                )}
                {denyCount > 0 && (
                  <span
                    className={darkMode ? 'text-gray-400' : 'text-gray-500'}
                    title={`${denyCount} denied tools`}
                  >
                    🚫 {denyCount} denied
                  </span>
                )}
              </div>
            </div>
          )}

          {/* ── 7. Metrics row (~56px) ───────────────────────────────────── */}
          {metrics && (
            <div
              className={`flex-shrink-0 border-t pt-2 mb-2 ${
                darkMode ? 'border-gray-700' : 'border-gray-100'
              }`}
              style={{ height: 56 }}
            >
              <div className="grid grid-cols-3 gap-1 text-center">
                <div
                  className={`rounded p-1 ${
                    darkMode ? 'bg-gray-700' : 'bg-gray-50'
                  }`}
                >
                  <div className="text-sm font-semibold text-blue-500">
                    {metrics.successRate}%
                  </div>
                  <div
                    className={`text-xs ${
                      darkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    Success
                  </div>
                </div>
                <div
                  className={`rounded p-1 ${
                    darkMode ? 'bg-gray-700' : 'bg-gray-50'
                  }`}
                >
                  <div className="text-sm font-semibold text-blue-500">
                    {metrics.avgResponseTime}
                  </div>
                  <div
                    className={`text-xs ${
                      darkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    Avg resp
                  </div>
                </div>
                <div
                  className={`rounded p-1 ${
                    darkMode ? 'bg-gray-700' : 'bg-gray-50'
                  }`}
                >
                  <div className="text-sm font-semibold text-blue-500">
                    {metrics.usageCount}
                  </div>
                  <div
                    className={`text-xs ${
                      darkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    Uses
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Legacy badge — shown when no forge profile ───────────────── */}
          {!hasEngineeringProfile && (
            <div className="flex-shrink-0 mb-2">
              <span
                className={`text-xs px-2 py-0.5 rounded-full ${
                  darkMode
                    ? 'bg-gray-700 text-gray-500'
                    : 'bg-gray-100 text-gray-400'
                }`}
              >
                Legacy · not yet forged
              </span>
            </div>
          )}

          {/* ── 8. Action row (mt-auto pins to bottom) ───────────────────── */}
          <div className="flex-shrink-0 mt-auto pt-2 border-t border-gray-700/30">
            {/* Two primary buttons */}
            <div className="grid grid-cols-2 gap-2 mb-2">
              {hasEngineeringProfile ? (
                <button
                  onClick={stop(() =>
                    navigate(`/personas/forge?personaId=${persona.id}`)
                  )}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold text-white transition-colors ${
                    darkMode
                      ? 'bg-cyan-700 hover:bg-cyan-600'
                      : 'bg-cyan-600 hover:bg-cyan-500'
                  }`}
                >
                  Edit in Forge
                </button>
              ) : (
                <button
                  onClick={stop(() =>
                    navigate(`/personas/forge?personaId=${persona.id}`)
                  )}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold text-white transition-colors ${
                    darkMode
                      ? 'bg-indigo-700 hover:bg-indigo-600'
                      : 'bg-indigo-600 hover:bg-indigo-500'
                  }`}
                >
                  Open in Forge
                </button>
              )}
              <button
                onClick={stop(() =>
                  navigate(`/chat/persona/${persona.id}`)
                )}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold text-white transition-colors ${
                  darkMode
                    ? 'bg-green-700 hover:bg-green-600'
                    : 'bg-green-600 hover:bg-green-500'
                }`}
              >
                Chat with
              </button>
            </div>

            {/* Secondary text links */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={stop(() => onViewDetails?.(persona.id))}
                  className={`text-xs ${
                    darkMode
                      ? 'text-gray-400 hover:text-gray-200'
                      : 'text-gray-500 hover:text-gray-800'
                  }`}
                >
                  Quick View
                </button>
                <span className={darkMode ? 'text-gray-700' : 'text-gray-300'}>·</span>
                <button
                  onClick={stop(() => onNavigateToDetails?.(persona.id))}
                  className={`text-xs ${
                    darkMode
                      ? 'text-gray-400 hover:text-gray-200'
                      : 'text-gray-500 hover:text-gray-800'
                  }`}
                >
                  Full Details
                </button>
                <span className={darkMode ? 'text-gray-700' : 'text-gray-300'}>·</span>
                <button
                  onClick={stop(() =>
                    navigate('/personas/forge', { state: { remixPersonaId: persona.id } })
                  )}
                  className={`text-xs ${
                    darkMode
                      ? 'text-violet-400 hover:text-violet-300'
                      : 'text-violet-500 hover:text-violet-700'
                  }`}
                  title="Create a remix of this persona"
                >
                  Remix
                </button>
              </div>

              <div className="flex items-center gap-2">
                {/* Delete — semi-hidden until card hover; dull dark red, brightens on hover */}
                {onDelete && (
                  <button
                    onClick={stop(() => onDelete(persona))}
                    title="Delete persona"
                    aria-label="Delete persona"
                    className={`text-xs inline-flex items-center gap-1 px-1.5 py-0.5 rounded
                      opacity-30 group-hover:opacity-100 transition-opacity duration-150
                      ${darkMode
                        ? 'text-red-700 hover:text-red-400 hover:bg-red-900/30'
                        : 'text-red-900 hover:text-red-600 hover:bg-red-50'
                      }`}
                  >
                    <FiTrash2 size={11} />
                    Delete
                  </button>
                )}
                {/* Migrate button — only shown when no engineering profile */}
                {!hasEngineeringProfile && (
                  <MigrateToForgeButton
                    personaId={persona.id}
                    hasEngineeringProfile={false}
                    compact={true}
                  />
                )}
              </div>
            </div>
          </div>

        </div>{/* end card body */}
      </div>{/* end accent strip + body wrapper */}
    </div>
  );
};
